import { useState, useRef } from 'react'

// Campo de edição; só existe enquanto a célula está em edição, então o rascunho
// sempre começa do valor atual. Enter/clicar fora salva, Esc cancela,
// Tab/Shift+Tab salva e vai para a célula seguinte/anterior.
function CellEditor({ value, type, options, align, onSave, onStop, onNavigate }) {

    const [ _draft, _setDraft ] = useState(value ?? '')

    // Garante um único commit (o blur pode disparar depois do Tab/Enter).
    const _doneRef = useRef(false)

    const _commit = (nextValue) => {

        if (_doneRef.current) return
        _doneRef.current = true

        const normalized = typeof nextValue === 'string' ? nextValue.trim() : nextValue

        if (String(normalized ?? '') !== String(value ?? '')) onSave(normalized === '' ? null : normalized)
    }

    const _cancel = () => {
        _doneRef.current = true
        onStop()
    }

    const _handleKeyDown = (event) => {

        if (event.key === 'Escape') { event.preventDefault(); _cancel() }

        if (event.key === 'Enter' && type !== 'select') { event.preventDefault(); _commit(_draft); onStop() }

        if (event.key === 'Tab') {
            event.preventDefault()
            _commit(_draft)
            onNavigate(event.shiftKey ? -1 : 1)
        }
    }

    const alignClass = align === 'left' ? 'text-left' : 'text-center'
    const inputClass = `w-full h-[28px] px-2 rounded-[4px] bg-white text-cinza-chumbo-e text-[0.8rem] shadow-[0_0_0_2px_#0090D0] focus:outline-none ${alignClass}`

    if (type === 'select') {

        return (
            <select
                autoFocus
                value={_draft ?? ''}
                onChange={(event) => { _setDraft(event.target.value); _commit(event.target.value); onStop() }}
                onBlur={() => { if (!_doneRef.current) _cancel() }}
                onKeyDown={_handleKeyDown}
                className={inputClass}
            >
                <option value="">—</option>
                {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
        )
    }

    return (
        <input
            autoFocus
            onFocus={(event) => event.target.select()}
            type={type === 'number' ? 'number' : 'text'}
            min={type === 'number' ? 0 : undefined}
            step={type === 'number' ? 1 : undefined}
            value={_draft}
            onChange={(event) => _setDraft(event.target.value)}
            onBlur={(event) => { if (!_doneRef.current) { _commit(event.target.value); onStop() } }}
            onKeyDown={_handleKeyDown}
            className={inputClass}
        />
    )
}

// Célula da Fila de Trens. Qual célula está em edição é controlado pela tabela
// (isEditing), para o Tab conseguir abrir a próxima.
// Sem permissão é uma célula comum, sem hover nem cursor.
function TrainQueueEditableCell({

    value,              // valor gravado (id, número ou texto)
    display,            // o que mostrar fora da edição (padrão: value)
    type = 'text',      // 'text' | 'number' | 'select'
    options = [],       // [{ value, label }] para select
    canEdit,
    isEditing,
    onStartEdit,
    onStopEdit,
    onNavigate,
    onSave,
    className = '',
    align = 'center',
    title

}) {

    const alignClass = align === 'left' ? 'text-left' : 'text-center'
    const editClass  = canEdit ? 'cursor-pointer hover:shadow-[inset_0_0_0_1px_#93BDE4]' : ''

    const shown   = display ?? value
    const isEmpty = shown === null || shown === undefined || shown === ''

    return (

        <td
            className={`px-2 h-[38px] border-b border-r border-[#e6ebf1] transition-shadow ${alignClass} ${editClass} ${className}`}
            onClick={() => { if (canEdit && !isEditing) onStartEdit() }}
            title={!isEditing ? title : undefined}
        >

            {!isEditing && (isEmpty ? <span className="text-cinza-chumbo-c">–</span> : shown)}

            {isEditing && (
                <CellEditor
                    value={value}
                    type={type}
                    options={options}
                    align={align}
                    onSave={onSave}
                    onStop={onStopEdit}
                    onNavigate={onNavigate}
                />
            )}

        </td>
    )
}

export default TrainQueueEditableCell
